import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/environments/environment';
import { UserService } from '@app/shared/user/state/user.service';
import {
    DuplicateCreativeSetRequest,
    ICreativeSetListItem,
    IDuplicationResponse,
    ISetsAndFolderItem,
    ISetsAndFolderItemResponseCLV,
    ISetsAndFoldersResponse
} from '@studio/domain';
import { from, map, Observable, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DuplicateCreativesDataService {
    readonly PAGE_SIZE = 200;

    constructor(
        private http: HttpClient,
        private userService: UserService
    ) {}

    getCreativeSetsWithFolders(
        brandId: string,
        folderId?: string,
        pageIndex: number = 1
    ): Observable<ISetsAndFoldersResponse> {
        return from(this.userService.hasPermission('CommonListView')).pipe(
            switchMap(isCommonListView => {
                if (isCommonListView) {
                    return this.getCreativeSetWithFoldersCLV(brandId, pageIndex, folderId);
                } else {
                    const url = `${environment.origins.sapi}/brands/${brandId}/folders/${
                        folderId ?? ''
                    }?pageSize=${this.PAGE_SIZE}&pageIndex=${pageIndex}`;
                    return this.getCreativeSetsWithFoldersB2(url);
                }
            })
        );
    }

    private getCreativeSetWithFoldersCLV(
        brandId: string,
        pageIndex: number,
        folderId?: string,
        searchTerm?: string
    ): Observable<ISetsAndFoldersResponse> {
        const url = `${environment.origins.listService}/api/${brandId}/creativesets/${
            searchTerm ? 'FilterCreativeSetsAndFolders' : 'GetCreativeSetAndFolders'
        }`;

        const requestBody: {
            paging: { pageNumber: number; pageSize: number };
            folderId?: string;
            filters?: { name: string };
        } = {
            paging: { pageNumber: pageIndex, pageSize: this.PAGE_SIZE }
        };

        if (folderId) {
            requestBody.folderId = folderId;
        }

        if (searchTerm) {
            requestBody.filters = { name: searchTerm };
        }

        return this.http
            .post<ISetsAndFolderItemResponseCLV>(url, requestBody)
            .pipe(map(data => this.transformData(data)));
    }

    private transformData(data: ISetsAndFolderItemResponseCLV): ISetsAndFoldersResponse {
        return {
            ...data,
            items: data.items.map(
                (item: ICreativeSetListItem): ISetsAndFolderItem => ({
                    id: item.type === 'CreativeSet' ? item.externalId : item.id,
                    name: item.name,
                    modified: item.modifiedAt,
                    created: item.createdAt,
                    type: item.type === 'CreativeSet' ? 'Creativeset' : item.type
                })
            ),
            pageIndex: data.currentPage,
            totalPages: Math.ceil(data.totalItemsCount / data.currentPageSize),
            totalCount: data.totalItemsCount,
            hasPreviousPage: data.currentPage > 1,
            hasNextPage: data.currentPage < Math.ceil(data.totalItemsCount / data.currentPageSize)
        };
    }

    private getCreativeSetsWithFoldersB2(url: string): Observable<ISetsAndFoldersResponse> {
        // page size being high to prevent the need for lazy load for now
        return this.http.get<ISetsAndFoldersResponse>(url);
    }

    duplicateToExisting(
        brandId: string,
        creativeset: string,
        sourceSizeIds: string[],
        sourceVersionIds: string[],
        targetCreativesetId: string
    ): Observable<IDuplicationResponse> {
        const headers = new HttpHeaders({ 'bf-brand-id': brandId });

        const url = `${environment.origins.sapi}/creativesets/${creativeset}/duplicate-to-existing/${targetCreativesetId}`;

        return this.http
            .post<IDuplicationResponse>(url, { sourceVersionIds, sourceSizeIds }, { headers })
            .pipe(map(() => ({ brandId, creativeSetId: targetCreativesetId })));
    }

    duplicateToNewB2(
        brandId: string,
        creativeset: string,
        sourceSizeIds: string[],
        sourceVersionIds: string[],
        targetFolderId?: string
    ): Observable<IDuplicationResponse> {
        const headers = new HttpHeaders({ 'bf-brand-id': brandId });

        const url = `${environment.origins.sapi}/creativesets/${creativeset}/duplicate-to-new`;

        return this.http.post<IDuplicationResponse>(
            url,
            { sourceVersionIds, sourceSizeIds, targetFolderId },
            { headers }
        );
    }

    duplicateToNew(
        duplicateCreativeSetRequest: DuplicateCreativeSetRequest
    ): Observable<IDuplicationResponse> {
        const url = `${environment.origins.listService}/api/${duplicateCreativeSetRequest.brandId}/creativesets/duplicateSizesAndVersions`;
        return this.http.post<IDuplicationResponse>(
            url,
            {
                externalId: duplicateCreativeSetRequest.creativesetId,
                targetParentFolderId: duplicateCreativeSetRequest.targetFolderId,
                sizeIds: duplicateCreativeSetRequest.sourceSizeIds,
                versionIds: duplicateCreativeSetRequest.sourceVersionIds
            },
            {
                headers: new HttpHeaders().set(
                    'actionCorrelationId',
                    duplicateCreativeSetRequest.actionCorrelationId
                )
            }
        );
    }

    search(
        brandId: string,
        searchTerm?: string,
        pageIndex: number = 1
    ): Observable<ISetsAndFoldersResponse> {
        return from(this.userService.hasPermission('CommonListView')).pipe(
            switchMap(isCommonListView => {
                if (isCommonListView) {
                    return this.getCreativeSetWithFoldersCLV(brandId, pageIndex, undefined, searchTerm);
                } else {
                    const url = `${environment.origins.sapi}/brands/${brandId}/folders/search/${searchTerm}?pageSize=${this.PAGE_SIZE}&pageIndex=${pageIndex}`;
                    return this.getCreativeSetsWithFoldersB2(url);
                }
            })
        );
    }
}
